import React from 'react';
import {
  VENT_HOOD_TOP_DEPTH,
  VENT_HOOD_LIP_HEIGHT,
  VENT_HOOD_GRILLE_WIDTH_RATIO,
  VENT_HOOD_GRILLE_HEIGHT_RATIO,
  VENT_HOOD_CHIMNEY_SIDE,
  isWallChimneyHood,
  woodHoodLayout,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';
import { hoodBuildOf } from '@shared-lib/fixture-types/hoodForm';
import { woodHoodShapeOf } from '@shared-lib/cabinet-runs/woodHoods';
import {
  Stroke,
  ChimneyHoodFrontView,
  ChimneyHoodSideView,
} from './VentHoodChimneyViews';
import {
  CurvedWoodHoodFrontView,
  CurvedWoodHoodSideView,
} from './CurvedWoodHoodViews';

/**
 * Vent-hood schematic — drawn from the SAME profile constants the 3D
 * `ventHoodRenderer` extrudes (proceduralSpecs.js):
 *
 *   - The body is full height and never tapers in WIDTH — only in depth.
 *     A FRONT elevation is therefore a rectangle with the bottom-lip
 *     edge line and the grille strip on the lip; the canopy read lives
 *     in the SIDE view, where the true cross-section shows: full-height
 *     back against the wall, a short vertical lip at the bottom front,
 *     and the slope rising to a cabinet-depth (12") top.
 *   - Floating (island) hoods taper symmetrically front-and-back and
 *     carry a 10"-square chimney that rises ABOVE the object box to the
 *     ceiling by the object's own `dimensions.chimneyHeight` (what
 *     `buildFloatingVentHood` measured to the ceiling); the box only
 *     contains the hood body, so the chase is drawn above y=0 and relies
 *     on overflow: visible. The elevation container clips it at the
 *     ceiling line (`ceilingClipPath`), so a chimney measured against a
 *     taller ceiling than this wall's cannot draw past it.
 *   - Wall-mount CHIMNEY hoods (`dimensions.chimneyHeight` without
 *     `floating`) are a pyramid canopy tapering in width AND depth under
 *     a telescoping duct cover drawn to its true height —
 *     `VentHoodChimneyViews.jsx`, from `chimneyHoodLayout`.
 *   - Under-cabinet inserts are a plain box in both views.
 *   - A Venus WOOD hood (the object's `hoodSku`, `hoodBuildOf`) is a box with
 *     its valance and the arch cut in it, from `woodHoodLayout` — the numbers
 *     the mesh is built from; the curved one (WRH) its chimney, flare and
 *     band, from `curvedWoodHoodLayout` (`CurvedWoodHoodViews.jsx`).
 */

const INCHES_TO_METERS = 0.0254;

// Only for a floating hood that carries NO measured `chimneyHeight` — a
// gallery card or hand-authored object. A hood built by the kitchen
// generator always states its own rise to the ceiling.
const FLOATING_CHASE_FALLBACK = 18 * INCHES_TO_METERS;

/** The chase a floating hood draws above its box: measured, else the stub. */
function floatingChaseHeight(obj) {
  const measured = obj?.dimensions?.chimneyHeight;
  return Number.isFinite(measured)
    ? Math.max(0, measured)
    : FLOATING_CHASE_FALLBACK;
}

export function resolveVentHoodVariant(obj = {}) {
  if (hoodBuildOf({ type: 'vent-hood', ...obj }) === 'wood') return 'wood';
  const subtype = obj.subtype;
  if (subtype === 'insert' || subtype === 'under-cabinet') return 'insert';
  if (obj.dimensions && obj.dimensions.floating === true) {
    return 'chimney-floating';
  }
  if (isWallChimneyHood(obj.dimensions)) return 'chimney-wall-ducted';
  return 'chimney-wall';
}

export function VentHoodSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  obj = {},
  strokeColor,
  strokeWidth,
}) {
  const variant = resolveVentHoodVariant(obj);
  const stroke = { strokeColor, strokeWidth };
  const isSide = view === 'left' || view === 'right';

  if (
    variant === 'wood' &&
    woodHoodShapeOf({ type: 'vent-hood', ...obj }) === 'curved'
  ) {
    const View = isSide ? CurvedWoodHoodSideView : CurvedWoodHoodFrontView;
    return (
      <View
        width={width}
        height={height}
        depth={depth}
        view={view}
        {...stroke}
      />
    );
  }

  if (variant === 'wood') {
    return isSide ? (
      <WoodHoodSideView depth={depth} height={height} view={view} {...stroke} />
    ) : (
      <WoodHoodFrontView width={width} height={height} {...stroke} />
    );
  }

  if (variant === 'chimney-wall-ducted') {
    const ChimneyView = isSide ? ChimneyHoodSideView : ChimneyHoodFrontView;
    return (
      <ChimneyView
        width={width}
        height={height}
        depth={depth}
        chimneyHeight={obj.dimensions.chimneyHeight}
        view={view}
        {...stroke}
      />
    );
  }

  if (isSide) {
    return (
      <VentHoodSideView
        depth={depth}
        height={height}
        variant={variant}
        chaseHeight={floatingChaseHeight(obj)}
        view={view}
        {...stroke}
      />
    );
  }
  return (
    <VentHoodFrontView
      width={width}
      height={height}
      variant={variant}
      chaseHeight={floatingChaseHeight(obj)}
      {...stroke}
    />
  );
}

VentHoodSchematic.supportsSideViews = true;

const svgFrame = (w, h) => ({
  viewBox: `0 0 ${w} ${h}`,
  preserveAspectRatio: 'xMidYMid meet',
  style: { width: '100%', height: '100%', overflow: 'visible' },
});

/** The ledge as a closed path from `x0` to `x1`, its top `top` (svg y down). */
const ledgePath = (x0, x1, top, thickness) =>
  `M ${x0} ${top} H ${x1} V ${top + thickness} H ${x0} Z`;

/**
 * A wood hood's front, as the catalog draws it (p.16): the box, the three
 * front panels above the ledge, the ledge proud of both sides, the side
 * boards either side of the apron and the arch cut in it.
 */
function WoodHoodFrontView({ width, height, ...stroke }) {
  const L = woodHoodLayout({ width, height });
  const { valanceHeight, archRise, foot, board, ledgeThickness } = L;
  const p = L.ledgeProjection;
  const top = height - valanceHeight - ledgeThickness;
  const rx = width / 2 - foot;
  return (
    <svg {...svgFrame(width, height)}>
      <Stroke d={`M 0 0 H ${width} V ${height} H 0 Z`} {...stroke} />
      {L.panels.map((panel, i) => (
        <Stroke
          key={i}
          data-part="wood-hood-panel"
          d={`M ${panel.x0} 0 H ${panel.x1} V ${L.panelHeight} H ${panel.x0} Z`}
          {...stroke}
        />
      ))}
      <Stroke
        data-part="wood-hood-ledge"
        d={ledgePath(-p, width + p, top, ledgeThickness)}
        {...stroke}
      />
      <Stroke
        data-part="wood-hood-side-boards"
        d={`M ${board} ${top + ledgeThickness} V ${height} M ${width - board} ${top + ledgeThickness} V ${height}`}
        {...stroke}
      />
      <Stroke
        data-part="wood-hood-arch"
        d={`M ${foot} ${height} A ${rx} ${archRise} 0 0 1 ${width - foot} ${height}`}
        {...stroke}
      />
    </svg>
  );
}

/**
 * A wood hood's side: the box and the ledge, proud of the front — x = 0 is
 * the front on a LEFT view, the back on a RIGHT one (`VentHoodSideView`).
 */
function WoodHoodSideView({ depth, height, view, ...stroke }) {
  const L = woodHoodLayout({ width: depth, height });
  const p = L.ledgeProjection;
  const top = height - L.valanceHeight - L.ledgeThickness;
  const [x0, x1] = view === 'right' ? [0, depth + p] : [-p, depth];
  return (
    <svg {...svgFrame(depth, height)}>
      <Stroke d={`M 0 0 H ${depth} V ${height} H 0 Z`} {...stroke} />
      <Stroke
        data-part="wood-hood-ledge"
        d={ledgePath(x0, x1, top, L.ledgeThickness)}
        {...stroke}
      />
    </svg>
  );
}

/**
 * Front elevation: the body silhouette is a rectangle (no width taper in
 * the mesh). The lip edge line marks where the slope begins, and the
 * grille strip sits on the lip, at the renderer's own proportions.
 */
function VentHoodFrontView({ width, height, variant, chaseHeight, ...stroke }) {
  const lip = Math.min(VENT_HOOD_LIP_HEIGHT, height * 0.5);
  const lipTopY = height - lip;
  const grilleW = width * VENT_HOOD_GRILLE_WIDTH_RATIO;
  const grilleH = lip * VENT_HOOD_GRILLE_HEIGHT_RATIO;
  const grilleX = (width - grilleW) / 2;
  const grilleY = height - lip / 2 - grilleH / 2;
  const tickCount = 8;
  const isFloating = variant === 'chimney-floating';
  const chimneyW = Math.min(VENT_HOOD_CHIMNEY_SIDE, width * 0.6);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {/* Body — full-width silhouette. */}
      <Stroke d={`M 0 0 H ${width} V ${height} H 0 Z`} {...stroke} />
      {/* Lip edge — where the sloped face meets the vertical lip. */}
      {variant !== 'insert' && (
        <Stroke d={`M 0 ${lipTopY} H ${width}`} {...stroke} />
      )}
      {/* Grille strip on the lip, with filter ticks. */}
      <Stroke
        d={`M ${grilleX} ${grilleY} h ${grilleW} v ${grilleH} h ${-grilleW} Z`}
        {...stroke}
      />
      {Array.from({ length: tickCount }, (_, i) => {
        const x = grilleX + ((i + 1) / (tickCount + 1)) * grilleW;
        return (
          <Stroke
            key={i}
            d={`M ${x} ${grilleY + grilleH * 0.2} V ${grilleY + grilleH * 0.8}`}
            strokeOpacity={0.6}
            {...stroke}
          />
        );
      })}
      {/* Floating hood: chimney chase rising above the body box, to the
          ceiling the hood was measured against. */}
      {isFloating && chaseHeight > 0 && (
        <Stroke
          d={`M ${(width - chimneyW) / 2} 0 v ${-chaseHeight} m ${chimneyW} 0 v ${chaseHeight}`}
          {...stroke}
        />
      )}
    </svg>
  );
}

/**
 * Side elevation: the renderer's exact cross-section. Convention (LEFT
 * view): x = 0 is the FRONT (room side), x = depth is the BACK (wall).
 * RIGHT mirrors.
 */
function VentHoodSideView({
  depth,
  height,
  variant,
  chaseHeight,
  view,
  ...stroke
}) {
  const isInsert = variant === 'insert';
  const isFloating = variant === 'chimney-floating';
  const topDepth = Math.min(VENT_HOOD_TOP_DEPTH, depth);
  const lip = Math.min(VENT_HOOD_LIP_HEIGHT, height * 0.5);
  const chimneyD = Math.min(VENT_HOOD_CHIMNEY_SIDE, depth * 0.6);

  let profile;
  if (isInsert) {
    profile = <Stroke d={`M 0 0 H ${depth} V ${height} H 0 Z`} {...stroke} />;
  } else if (isFloating) {
    // Symmetric lampshade: both faces slope in to a chimney-deep top.
    const topX0 = (depth - chimneyD) / 2;
    profile = (
      <g>
        <Stroke
          d={[
            `M 0 ${height}`,
            `L ${depth} ${height}`,
            `L ${topX0 + chimneyD} 0`,
            `L ${topX0} 0`,
            'Z',
          ].join(' ')}
          {...stroke}
        />
        {chaseHeight > 0 && (
          <Stroke
            d={`M ${topX0} 0 v ${-chaseHeight} m ${chimneyD} 0 v ${chaseHeight}`}
            {...stroke}
          />
        )}
      </g>
    );
  } else {
    // Wall-mounted: bottom full depth → back full height → top runs
    // 12" forward from the wall → slope down to the lip top at the
    // front → lip down to the bottom. One closed outline, exactly the
    // shape the mesh extrudes.
    profile = (
      <Stroke
        d={[
          `M 0 ${height}`,
          `L ${depth} ${height}`,
          `L ${depth} 0`,
          `L ${depth - topDepth} 0`,
          `L 0 ${height - lip}`,
          'Z',
        ].join(' ')}
        {...stroke}
      />
    );
  }

  return (
    <svg
      viewBox={`0 0 ${depth} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {view === 'right' ? (
        <g transform={`translate(${depth}, 0) scale(-1, 1)`}>{profile}</g>
      ) : (
        profile
      )}
    </svg>
  );
}
